import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatearCLP, formatearPorcentajeSimple } from '@lexicore/shared';
import { Cargando, ErrorCarga } from '../componentes/UI';
import { useAuth } from '../contextos/AuthContext';
import {
  useCartera,
  useCompetencia,
  useDesempenoCategorias,
  useProyectos,
  useResumenGeneral,
} from '../lib/consultas';
import '../estilos/reporte.css';

/**
 * Reporte ejecutivo, pensado para imprimirse.
 *
 * Se genera en el navegador y no en el servidor a propósito. Un PDF en servidor
 * exigiría llevar un Chromium headless al VPS —unos 300 MB, más actualizaciones
 * de seguridad y otra cosa que puede caerse— para producir lo mismo que el
 * navegador ya sabe hacer con «Guardar como PDF». El CRM anterior resolvía esto
 * igual, abriendo una ventana e imprimiendo, y era la decisión correcta.
 *
 * A cambio, el reporte se ve en pantalla antes de imprimir, se puede revisar, y
 * hereda el tema claro para que no salga con fondo negro.
 */
export function Reporte() {
  const navegar = useNavigate();
  const { usuario } = useAuth();

  const general = useResumenGeneral();
  const categorias = useDesempenoCategorias();
  const proyectos = useProyectos();
  const cartera = useCartera();
  const competencia = useCompetencia();

  // El reporte se imprime siempre en claro: un informe con fondo negro gasta
  // tinta y se lee mal en papel.
  useEffect(() => {
    const anterior = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (anterior) document.documentElement.setAttribute('data-theme', anterior);
    };
  }, []);

  if (general.isLoading || categorias.isLoading) return <Cargando texto="Preparando el reporte…" />;
  if (general.isError) return <ErrorCarga error={general.error} reintentar={() => void general.refetch()} />;
  if (!general.data) return null;

  const g = general.data;
  const hoy = new Date().toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });

  const activos = (proyectos.data ?? []).filter((p) => p.estado === 'EN_EJECUCION' || p.estado === 'PLANIFICADO');
  const enRiesgo = activos.filter((p) => p.metricas.alertas.length > 0);

  const rankingUtilidad = [...(proyectos.data ?? [])]
    .sort((a, b) => b.metricas.utilidad - a.metricas.utilidad)
    .slice(0, 10);

  return (
    <div className="reporte">
      <div className="reporte-barra no-imprimir">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => navegar(-1)}>
          Volver
        </button>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Usa «Guardar como PDF» en el diálogo de impresión para archivarlo.
        </span>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
          Imprimir o guardar en PDF
        </button>
      </div>

      <header className="reporte-cabecera">
        <div>
          <h1>{usuario?.tenantNombre ?? 'LEXICORE LABS SYSTEM'}</h1>
          <p className="reporte-sub">Reporte ejecutivo · generado el {hoy}</p>
        </div>
        <div className="reporte-logo">L</div>
      </header>

      {/* ══ Resumen ══ */}
      <section>
        <h2>Resumen general</h2>
        <div className="reporte-kpis">
          {[
            { etiqueta: 'Total adjudicado', valor: formatearCLP(g.resultados.totalAdjudicado) },
            { etiqueta: 'Costos de proyecto', valor: formatearCLP(g.finanzas.costosProyecto) },
            { etiqueta: 'Utilidad bruta', valor: formatearCLP(g.finanzas.utilidadBruta) },
            { etiqueta: 'Gastos generales', valor: formatearCLP(g.finanzas.costosGenerales) },
            { etiqueta: 'Resultado operacional', valor: formatearCLP(g.finanzas.resultadoOperacional), destacado: true },
            {
              etiqueta: 'Margen bruto',
              valor: g.finanzas.margenBrutoPct === null ? '—' : formatearPorcentajeSimple(g.finanzas.margenBrutoPct, 1),
            },
            {
              etiqueta: 'Tasa de éxito',
              valor: g.resultados.winRatePct === null ? 'sin datos' : formatearPorcentajeSimple(g.resultados.winRatePct),
            },
            { etiqueta: 'Ingreso recurrente mensual', valor: formatearCLP(g.finanzas.mrr) },
          ].map((kpi) => (
            <div key={kpi.etiqueta} className={`reporte-kpi${kpi.destacado ? ' destacado' : ''}`}>
              <span className="reporte-kpi-etiqueta">{kpi.etiqueta}</span>
              <strong>{kpi.valor}</strong>
            </div>
          ))}
        </div>

        <p className="reporte-nota">
          El resultado operacional descuenta tanto los costos de proyecto como los gastos generales de la empresa. Es el
          número que dice si la operación completa gana plata, no solo si los proyectos tienen buen margen.
        </p>
      </section>

      {/* ══ Cartera ══ */}
      <section>
        <h2>Estado de la cartera</h2>
        <table className="reporte-tabla">
          <thead>
            <tr>
              <th>Concepto</th>
              <th className="derecha">Monto</th>
              <th className="derecha">Detalle</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Facturado</td>
              <td className="derecha mono">{formatearCLP(cartera.data?.totalFacturado ?? 0)}</td>
              <td className="derecha tenue">—</td>
            </tr>
            <tr>
              <td>Cobrado</td>
              <td className="derecha mono">{formatearCLP(g.cobranza.totalCobrado)}</td>
              <td className="derecha tenue">
                {(cartera.data?.totalFacturado ?? 0) > 0
                  ? `${formatearPorcentajeSimple((g.cobranza.totalCobrado / cartera.data!.totalFacturado) * 100, 1)} de lo facturado`
                  : '—'}
              </td>
            </tr>
            <tr>
              <td>Por cobrar</td>
              <td className="derecha mono">{formatearCLP(g.cobranza.porCobrar)}</td>
              <td className="derecha tenue">
                {g.cobranza.vencido > 0 ? `${formatearCLP(g.cobranza.vencido)} ya vencidos` : 'nada vencido'}
              </td>
            </tr>
            <tr>
              <td>Días promedio de cobro</td>
              <td className="derecha mono">
                {g.cobranza.diasPromedioCobro === null ? '—' : Math.round(g.cobranza.diasPromedioCobro)}
              </td>
              <td className="derecha tenue">ponderado por monto</td>
            </tr>
          </tbody>
        </table>

        {g.cobranza.tramos.some((t) => t.cantidad > 0) && (
          <>
            <h3>Antigüedad de lo pendiente</h3>
            <table className="reporte-tabla">
              <thead>
                <tr>
                  <th>Tramo</th>
                  <th className="derecha">Documentos</th>
                  <th className="derecha">Monto</th>
                </tr>
              </thead>
              <tbody>
                {g.cobranza.tramos
                  .filter((tramo) => tramo.cantidad > 0)
                  .map((tramo) => (
                    <tr key={tramo.clave}>
                      <td>{tramo.etiqueta}</td>
                      <td className="derecha mono">{tramo.cantidad}</td>
                      <td className="derecha mono">{formatearCLP(tramo.monto)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {/* ══ Categorías ══ */}
      <section className="salto-pagina">
        <h2>Desempeño por línea de negocio</h2>
        <table className="reporte-tabla">
          <thead>
            <tr>
              <th>Categoría</th>
              <th className="derecha">Adjudicadas</th>
              <th className="derecha">Ingresos</th>
              <th className="derecha">Costos</th>
              <th className="derecha">Utilidad</th>
              <th className="derecha">Margen real</th>
              <th className="derecha">Objetivo</th>
            </tr>
          </thead>
          <tbody>
            {(categorias.data ?? [])
              .filter((c) => c.total > 0)
              .map((categoria) => (
                <tr key={categoria.id}>
                  <td>{categoria.nombre}</td>
                  <td className="derecha mono">{categoria.adjudicadas}</td>
                  <td className="derecha mono">{formatearCLP(categoria.ingresos)}</td>
                  <td className="derecha mono">{formatearCLP(categoria.costos)}</td>
                  <td className="derecha mono">{formatearCLP(categoria.utilidad)}</td>
                  <td className="derecha mono">
                    {categoria.margenRealPct === null ? '—' : formatearPorcentajeSimple(categoria.margenRealPct, 1)}
                  </td>
                  <td className="derecha tenue mono">{categoria.margenObjetivoPct}%</td>
                </tr>
              ))}
          </tbody>
        </table>
        <p className="reporte-nota">
          El margen real usa solo los costos efectivamente cargados. Una categoría sin gastos registrados aparentará
          margen completo, que nunca es cierto.
        </p>
      </section>

      {/* ══ Proyectos ══ */}
      <section>
        <h2>Proyectos por utilidad</h2>
        <table className="reporte-tabla">
          <thead>
            <tr>
              <th>Proyecto</th>
              <th>Organismo</th>
              <th className="derecha">Contrato</th>
              <th className="derecha">Costos</th>
              <th className="derecha">Utilidad</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {rankingUtilidad.map((proyecto) => (
              <tr key={proyecto.id}>
                <td>{proyecto.nombre}</td>
                <td className="tenue">{proyecto.licitacion?.organismo.nombre ?? '—'}</td>
                <td className="derecha mono">{formatearCLP(proyecto.montoContratoNeto)}</td>
                <td className="derecha mono">{formatearCLP(proyecto.metricas.costoReal)}</td>
                <td className="derecha mono">{formatearCLP(proyecto.metricas.utilidad)}</td>
                <td className="tenue">{proyecto.estado.replace('_', ' ').toLowerCase()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ══ Atención ══ */}
      {enRiesgo.length > 0 && (
        <section>
          <h2>Requieren atención</h2>
          <ul className="reporte-lista">
            {enRiesgo.map((proyecto) => (
              <li key={proyecto.id}>
                <strong>{proyecto.nombre}</strong>
                <br />
                {proyecto.metricas.alertas.map((alerta) => alerta.texto).join(' · ')}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ══ Competencia ══ */}
      {competencia.data && competencia.data.derrotas > 0 && (
        <section>
          <h2>Inteligencia competitiva</h2>
          <p className="reporte-nota" style={{ marginTop: 0 }}>
            {competencia.data.derrotas} licitación(es) perdidas por {formatearCLP(competencia.data.dineroPerdido)} en
            pipeline.
            {competencia.data.brechaPrecioPromedioPct !== null &&
              ` En promedio, ofertamos ${formatearPorcentajeSimple(Math.abs(competencia.data.brechaPrecioPromedioPct), 1)} ${
                competencia.data.brechaPrecioPromedioPct > 0 ? 'por sobre' : 'por debajo de'
              } quien ganó.`}
          </p>

          {competencia.data.rivales.length > 0 && (
            <table className="reporte-tabla">
              <thead>
                <tr>
                  <th>Competidor</th>
                  <th className="derecha">Victorias</th>
                  <th className="derecha">Monto ganado</th>
                  <th className="derecha">Brecha promedio</th>
                </tr>
              </thead>
              <tbody>
                {competencia.data.rivales.slice(0, 8).map((rival) => (
                  <tr key={rival.id}>
                    <td>{rival.nombre}</td>
                    <td className="derecha mono">{rival.victorias}</td>
                    <td className="derecha mono">{formatearCLP(rival.montoGanado)}</td>
                    <td className="derecha mono">
                      {rival.brechaPromedioPct === null ? '—' : formatearPorcentajeSimple(rival.brechaPromedioPct, 1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <footer className="reporte-pie">
        {usuario?.tenantNombre ?? 'LEXICORE LABS SYSTEM LIMITADA'} · Reporte generado el {hoy} desde el CRM interno.
        Cifras en pesos chilenos, netos de IVA.
      </footer>
    </div>
  );
}
